// src/components/CartPage.js
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import { createOrder } from '../api/orders';
import { AWAITING_STORE_PRICE_CAPTION } from '../utils/buylistTakesNoPrice';
import { looksLikeAnEmail } from '../utils/emailShape';
import {
    AppBar,
    Toolbar,
    IconButton,
    Container,
    Typography,
    Box,
    Button,
    Divider,
    List,
    ListItem,
    Avatar,
    ListItemText,
    Paper,
    Fade,
    Stack,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Chip,
    ToggleButtonGroup,
    ToggleButton,
    Grid,
    Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    ArrowBack as ArrowBackIcon,
    RemoveCircleOutline as RemoveIcon,
    AddCircleOutline as AddIcon,
    Close as CloseIcon,
    ShoppingCart as ShoppingCartIcon,
    Sell as SellIcon,
    Print as PrintIcon,
} from '@mui/icons-material';
import { useCart } from '../contexts/CartContext';
import { useKioskTheme } from '../contexts/KioskThemeContext';
import { variantKey } from '../utils/variantKey';
import { cartLineImage } from '../utils/cardArt';
import { describeAppliedRefusal } from '../utils/buylistRefusal';
import { printReceiptHtmlViaQZ, initQZ } from '../utils/qzReceiptPrinter';
import { printReceiptHtmlViaBrowser } from '../utils/browserReceiptPrinter';
import { buildReceiptItemLines } from '../utils/receiptItemLines';
import { gradedLineLabel, isGradedVariant } from '../utils/buylistGraded';
import useKeyboardInset from '../utils/useKeyboardInset';
import ErrorBoundary from './ErrorBoundary';
import KioskKeyboard from './KioskKeyboard';
import CartOptimizerPanel from './CartOptimizerPanel';

/** ────────────────────────────────────────────────────────────────────────── */
/**                     SELL CART ITEM (memoized) - Customer buying from store  */
/** ────────────────────────────────────────────────────────────────────────── */
const SellCartItem = React.memo(({ item, onUpdateQuantity, onRemove }) => {
    const theme = useTheme();
    const { formatMoney } = useKioskTheme();
    // The copy's own scan, else the art for its printing (TKT-2026-107421).
    const imageSrc = cartLineImage(item) || undefined;

    // Target the line by its full variant identity. A bare skuId can name two
    // lines at once now that a graded slab and a raw copy are separate rows on
    // the same SKU, and every one of these would move both. TKT-2026-105367.
    const key = variantKey(item.variant);

    const handleDecrease = useCallback(() => {
        const newQty = Math.max(1, item.qty - 1);
        if (newQty !== item.qty) {
            onUpdateQuantity(key, newQty, 'sell');
        }
    }, [key, item.qty, onUpdateQuantity]);

    const handleIncrease = useCallback(() => {
        onUpdateQuantity(key, item.qty + 1, 'sell');
    }, [key, item.qty, onUpdateQuantity]);

    const handleRemove = useCallback(() => {
        onRemove(key, 'sell');
    }, [key, onRemove]);

    const unitPrice = useMemo(() => formatMoney(item.price), [formatMoney, item.price]);
    const itemTotal = useMemo(() => formatMoney(item.price * item.qty), [formatMoney, item.price, item.qty]);

    return (
        <ListItem
            disableGutters
            sx={{
                py: 2,
                px: { xs: 1.5, md: 2.5 },
                mb: 2,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                borderRadius: '12px',
                border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.08 : 0.2)}`,
                background:
                    theme.palette.background.ownerCard
                    || (theme.palette.mode === 'light'
                        ? 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(237,239,247,0.92) 100%)'
                        : 'linear-gradient(135deg, rgba(30,30,36,0.96) 0%, rgba(64,66,77,0.9) 100%)'),
                boxShadow: '0 14px 28px rgba(0,0,0,0.12)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '@media (hover: hover)': {
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 20px 40px rgba(62,172,253,0.18)',
                    },
                },
                '&:last-of-type': { mb: 0 },
            }}
        >
            <Avatar
                variant='rounded'
                src={imageSrc}
                alt={item.cleanName}
                sx={{
                    width: { xs: 48, md: 56 },
                    height: { xs: 48, md: 56 },
                    mr: 2,
                    flexShrink: 0,
                    borderRadius: '10px',
                    border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.1 : 0.25)}`,
                    backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.06 : 0.25),
                    '& img': { objectFit: 'contain', p: 0.5 },
                }}
            >
                <ShoppingCartIcon />
            </Avatar>

            <ListItemText
                primary={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                        <Typography
                            variant='subtitle1'
                            fontWeight='medium'
                            sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {item.cleanName}
                        </Typography>
                        <Typography variant='caption' color='text.secondary' noWrap>
                            {item.variant.condAbbr} / {item.variant.printing}
                            {item.variant.langAbbr && item.variant.langAbbr !== 'EN' ? ` / ${item.variant.langAbbr}` : ''}
                        </Typography>
                    </Box>
                }
                sx={{ flex: '1 1 auto', mr: 1, minWidth: 0 }}
            />

            <IconButton
                edge='end'
                onClick={handleRemove}
                aria-label={`Remove ${item.cleanName} from cart`}
                sx={{
                    color: 'error.main',
                    minWidth: 44,
                    minHeight: 44,
                    '@media (hover: hover)': {
                        '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.08) },
                    },
                }}
            >
                <CloseIcon />
            </IconButton>

            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1.5, md: 2 },
                width: '100%',
                mt: 1.5,
                pl: { xs: 0, md: 8.5 },
                justifyContent: 'space-between',
            }}>
                <Paper
                    variant='outlined'
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 1,
                        py: 0.5,
                        backgroundColor: 'background.default',
                        borderRadius: '10px',
                    }}
                >
                    <IconButton
                        size='small'
                        disabled={item.qty <= 1}
                        onClick={handleDecrease}
                        aria-label={`Decrease quantity of ${item.cleanName}`}
                        // No `opacity` on the disabled state. MUI already paints a
                        // disabled IconButton at action.disabled, which is 0.3
                        // alpha, and an `opacity: 0.3` on top of that multiplies to
                        // 0.09. A cart holding one copy of a card with one in stock
                        // disables BOTH arrows, so the whole stepper rendered at
                        // 1.2:1 and looked like an empty pill. TKT-2026-105169.
                        sx={{ color: 'primary.onSurface', minWidth: 36, minHeight: 36 }}
                    >
                        <RemoveIcon fontSize='small' />
                    </IconButton>

                    <Typography sx={{ mx: 1.5, minWidth: 24, textAlign: 'center', fontWeight: 'medium', fontSize: '1.1rem' }}>
                        {item.qty}
                    </Typography>

                    <IconButton
                        size='small'
                        disabled={item.qty >= item.variant.quantity}
                        onClick={handleIncrease}
                        aria-label={`Increase quantity of ${item.cleanName}`}
                        sx={{ color: 'primary.onSurface', minWidth: 36, minHeight: 36 }}
                    >
                        <AddIcon fontSize='small' />
                    </IconButton>
                </Paper>

                <Box sx={{ textAlign: 'right' }}>
                    <Typography variant='body2' color='text.secondary'>
                        {unitPrice} each
                    </Typography>
                    <Typography variant='subtitle2' color='primary.onSurface' fontWeight='medium'>
                        Subtotal: {itemTotal}
                    </Typography>
                </Box>
            </Box>
        </ListItem>
    );
});
SellCartItem.displayName = 'SellCartItem';

SellCartItem.propTypes = {
    item: PropTypes.shape({
        productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        cdnImage: PropTypes.string,
        cleanName: PropTypes.string.isRequired,
        price: PropTypes.number.isRequired,
        qty: PropTypes.number.isRequired,
        variant: PropTypes.shape({
            skuId: PropTypes.number.isRequired,
            condAbbr: PropTypes.string.isRequired,
            printing: PropTypes.string.isRequired,
            quantity: PropTypes.number.isRequired,
        }).isRequired,
    }).isRequired,
    onUpdateQuantity: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
};

/** ────────────────────────────────────────────────────────────────────────── */
/**                     BUY CART ITEM (memoized) - Store buying from customer   */
/** ────────────────────────────────────────────────────────────────────────── */
const BuyCartItem = React.memo(({ item, onUpdateQuantity, onRemove, onUpdatePaymentType }) => {
    const theme = useTheme();
    const { formatMoney } = useKioskTheme();
    // The art for the printing being sold (TKT-2026-107421).
    const imageSrc = cartLineImage(item) || undefined;

    const key = variantKey(item.variant);

    const handleDecrease = useCallback(() => {
        const newQty = Math.max(1, item.qty - 1);
        if (newQty !== item.qty) {
            onUpdateQuantity(key, newQty, 'buy', item.productId, item.paymentType);
        }
    }, [key, item.qty, item.productId, item.paymentType, onUpdateQuantity]);

    const handleIncrease = useCallback(() => {
        onUpdateQuantity(key, item.qty + 1, 'buy', item.productId, item.paymentType);
    }, [key, item.qty, item.productId, item.paymentType, onUpdateQuantity]);

    const handleRemove = useCallback(() => {
        onRemove(key, 'buy', item.productId, item.paymentType);
    }, [key, item.productId, item.paymentType, onRemove]);

    const handlePaymentTypeChange = useCallback((_, newType) => {
        if (newType) {
            onUpdatePaymentType(key, newType, item.productId, item.paymentType);
        }
    }, [key, item.productId, item.paymentType, onUpdatePaymentType]);

    const displayPrice = item.paymentType === 'Credit' ? item.buylistCreditPrice : item.buylistCashPrice;
    const itemTotal = useMemo(() => formatMoney(displayPrice * item.qty), [formatMoney, displayPrice, item.qty]);
    const slab = isGradedVariant(item.variant);

    return (
        <ListItem
            disableGutters
            sx={{
                py: 2,
                px: { xs: 1.5, md: 2.5 },
                mb: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                borderRadius: '12px',
                border: `2px solid ${theme.palette.success.main}`,
                // Buy mode tints the card green. On a store that set its own Card
                // Color the tint goes OVER their surface rather than replacing it
                // with a stock pale green. TKT-2026-105169.
                background:
                    theme.palette.background.ownerCard
                        ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.28)} 0%, ${alpha(theme.palette.success.main, 0.16)} 100%), ${theme.palette.background.ownerCard}`
                        : theme.palette.mode === 'light'
                            ? 'linear-gradient(135deg, rgba(232,245,233,0.98) 0%, rgba(200,230,201,0.92) 100%)'
                            : 'linear-gradient(135deg, rgba(27,94,32,0.3) 0%, rgba(46,125,50,0.2) 100%)',
                boxShadow: `0 14px 28px ${alpha(theme.palette.success.main, 0.15)}`,
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '@media (hover: hover)': {
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 20px 40px ${alpha(theme.palette.success.main, 0.25)}`,
                    },
                },
                '&:last-of-type': { mb: 0 },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Avatar
                    variant='rounded'
                    src={imageSrc}
                    alt={item.cleanName}
                    sx={{
                        width: { xs: 48, md: 56 },
                        height: { xs: 48, md: 56 },
                        mr: 2,
                        flexShrink: 0,
                        borderRadius: '10px',
                        border: `2px solid ${theme.palette.success.main}`,
                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                        '& img': { objectFit: 'contain', p: 0.5 },
                    }}
                >
                    <SellIcon />
                </Avatar>

                <ListItemText
                    primary={
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                            <Typography
                                variant='subtitle1'
                                fontWeight='medium'
                                sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                }}
                            >
                                {item.cleanName}
                            </Typography>
                            <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
                                <Chip
                                    label="WE'RE BUYING"
                                    size="small"
                                    sx={{
                                        backgroundColor: theme.palette.success.main,
                                        color: '#fff',
                                        fontWeight: 700,
                                        fontSize: '0.65rem'
                                    }}
                                />
                                <Typography variant='caption' color='text.secondary' noWrap>
                                    {gradedLineLabel(item.variant) || <>{item.variant.condAbbr} / {item.variant.printing}</>}
                                    {item.variant.certNumber ? ` · Cert ${item.variant.certNumber}` : ''}
                                </Typography>
                            </Stack>
                            {item.awaitingStorePrice === true && !(item.buylistCashPrice > 0) && !(item.buylistCreditPrice > 0) && (
                                <Typography variant='caption' color='text.secondary' display='block'>
                                    {AWAITING_STORE_PRICE_CAPTION}
                                </Typography>
                            )}
                        </Box>
                    }
                    sx={{ flex: '1 1 auto', mr: 1, minWidth: 0 }}
                />

                <IconButton
                    edge='end'
                    onClick={handleRemove}
                    aria-label={`Remove ${item.cleanName} from buylist`}
                    sx={{
                        color: 'error.main',
                        minWidth: 44,
                        minHeight: 44,
                        '@media (hover: hover)': {
                            '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.08) },
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, width: '100%', flexWrap: 'wrap', gap: 1 }}>
                <ToggleButtonGroup
                    value={item.paymentType}
                    exclusive
                    onChange={handlePaymentTypeChange}
                    size='small'
                    sx={{
                        '& .MuiToggleButton-root': {
                            minHeight: 36,
                            fontSize: { xs: '0.8rem', md: '0.875rem' },
                        },
                        '& .MuiToggleButton-root.Mui-selected': {
                            backgroundColor: theme.palette.success.main,
                            color: theme.palette.success.contrastText,
                            '&:hover': { backgroundColor: theme.palette.success.dark }
                        }
                    }}
                >
                    {/* A slab is never moved onto a leg the store pays nothing on
                        (TKT-2026-107322, as the buylist site adds one). */}
                    <ToggleButton value='Cash' disabled={slab && !(item.buylistCashPrice > 0)}>
                        Cash {formatMoney(item.buylistCashPrice, { extraDigit: 'subunit' })}
                    </ToggleButton>
                    <ToggleButton value='Credit' disabled={slab && !(item.buylistCreditPrice > 0)}>
                        Credit {formatMoney(item.buylistCreditPrice, { extraDigit: 'subunit' })}
                    </ToggleButton>
                </ToggleButtonGroup>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
                    <Paper
                        variant='outlined'
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 1,
                            py: 0.5,
                            backgroundColor: 'background.default',
                            borderRadius: '10px',
                            borderColor: theme.palette.success.main,
                        }}
                    >
                        <IconButton
                            size='small'
                            disabled={item.qty <= 1}
                            onClick={handleDecrease}
                            // See the sell-cart stepper above: the extra opacity
                            // multiplies with MUI's own disabled alpha.
                            // TKT-2026-105169.
                            sx={{ color: theme.palette.success.main, minWidth: 36, minHeight: 36 }}
                        >
                            <RemoveIcon fontSize='small' />
                        </IconButton>

                        <Typography sx={{ mx: 1.5, minWidth: 24, textAlign: 'center', fontWeight: 'medium', fontSize: '1.1rem' }}>
                            {item.qty}
                        </Typography>

                        <IconButton
                            size='small'
                            // One cert number is one slab (TKT-2026-107322).
                            disabled={Boolean(item.variant.certNumber)}
                            onClick={handleIncrease}
                            sx={{ color: theme.palette.success.main, minWidth: 36, minHeight: 36 }}
                        >
                            <AddIcon fontSize='small' />
                        </IconButton>
                    </Paper>

                    <Box sx={{ textAlign: 'right' }}>
                        <Typography variant='body2' color='text.secondary'>
                            {formatMoney(displayPrice, { extraDigit: 'subunit' })} each
                        </Typography>
                        <Typography variant='subtitle2' sx={{ color: theme.palette.success.main }} fontWeight='bold'>
                            We Pay: {itemTotal}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </ListItem>
    );
});
BuyCartItem.displayName = 'BuyCartItem';

BuyCartItem.propTypes = {
    item: PropTypes.shape({
        productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        cdnImage: PropTypes.string,
        cleanName: PropTypes.string.isRequired,
        qty: PropTypes.number.isRequired,
        paymentType: PropTypes.oneOf(['Cash', 'Credit']).isRequired,
        buylistCashPrice: PropTypes.number,
        buylistCreditPrice: PropTypes.number,
        variant: PropTypes.shape({
            // A raw Sell To Us line's is '<printing>-<condition>', a slab's is null.
            skuId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            condAbbr: PropTypes.string.isRequired,
            printing: PropTypes.string.isRequired,
            gradingCompany: PropTypes.string,
            certNumber: PropTypes.string,
        }).isRequired,
    }).isRequired,
    onUpdateQuantity: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    onUpdatePaymentType: PropTypes.func.isRequired,
};

/** ────────────────────────────────────────────────────────────────────────── */
/**                           EMPTY CART (memoized)                           */
/** ────────────────────────────────────────────────────────────────────────── */
const EmptyCart = React.memo(({ onBrowseProducts }) => (
    <Fade in timeout={600}>
        <Paper
            elevation={0}
            sx={(theme) => ({
                textAlign: 'center',
                py: 8,
                px: { xs: 3, md: 6 },
                borderRadius: '12px',
                background:
                    theme.palette.background.ownerCard
                    || (theme.palette.mode === 'light'
                        ? 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(237,239,247,0.9) 100%)'
                        : 'linear-gradient(135deg, rgba(30,30,36,0.95) 0%, rgba(64,66,77,0.9) 100%)'),
                border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.12 : 0.24)}`,
                boxShadow: '0 28px 64px rgba(62,172,253,0.16)',
            })}
        >
            <ShoppingCartIcon sx={{ fontSize: 76, color: 'primary.onSurface', mb: 2 }} />
            <Typography variant='h4' gutterBottom fontWeight={700} letterSpacing='-0.01em'>
                Your cart is feeling light
            </Typography>
            <Typography variant='body1' color='text.secondary' sx={{ mb: 4 }}>
                Explore the catalog to add products and build a ready-to-collect order.
            </Typography>
            <Button variant='contained' size='large' onClick={onBrowseProducts} sx={{ textTransform: 'none', fontWeight: 600, px: 4 }}>
                Browse products
            </Button>
        </Paper>
    </Fade>
));
EmptyCart.displayName = 'EmptyCart';
EmptyCart.propTypes = {
    onBrowseProducts: PropTypes.func.isRequired,
};

/** ────────────────────────────────────────────────────────────────────────── */
/**                            CART PAGE COMPONENT                            */
/** ────────────────────────────────────────────────────────────────────────── */
export default function CartPage() {
    const theme = useTheme();
    const { themeOverrides, formatMoney } = useKioskTheme();
    const { storeName } = useParams();
    const navigate = useNavigate();
    const {
        cart,
        updateQuantity,
        removeFromCart,
        clearCart,
        sellCartItems,
        buyCartItems,
        buyTotalCash,
        buyTotalCredit,
        updatePaymentType,
        setTransactionInProgress,
        applyBuylistRefusal,
        applyBookedBuyLines,
        orderLimit,
    } = useCart();
    // TKT-2026-105480. Over the store's order size limit, checkout would be
    // refused by the server, so it is not offered; the banner says why.
    const overOrderLimit = orderLimit?.over === true;

    // State for unified checkout dialog
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [customerName, setCustomerName] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [successCode, setSuccessCode] = useState(null);
    const [orderHasBuyItems, setOrderHasBuyItems] = useState(false);
    const [finalizeCheckoutAfterClose, setFinalizeCheckoutAfterClose] = useState(false);

    // Memoized calculations for sell items
    const { sellTotalItems, sellTotalPrice, sellTotalPriceNum } = useMemo(() => {
        const totalItemsCalc = sellCartItems.reduce((sum, item) => sum + item.qty, 0);
        const totalPriceCalc = sellCartItems.reduce((sum, item) => sum + item.price * item.qty, 0);
        return {
            sellTotalItems: totalItemsCalc,
            sellTotalPrice: formatMoney(totalPriceCalc),
            sellTotalPriceNum: totalPriceCalc,
        };
    }, [sellCartItems, formatMoney]);

    // Memoized calculations for buy items
    const { buyTotalItems, buyTotalCashFormatted, buyTotalCreditFormatted, buyCashItemCount, buyCreditItemCount } = useMemo(() => {
        const totalItemsCalc = buyCartItems.reduce((sum, item) => sum + item.qty, 0);
        const cashItems = buyCartItems.filter(i => i.paymentType === 'Cash');
        const creditItems = buyCartItems.filter(i => i.paymentType === 'Credit');
        return {
            buyTotalItems: totalItemsCalc,
            buyTotalCashFormatted: formatMoney(buyTotalCash),
            buyTotalCreditFormatted: formatMoney(buyTotalCredit),
            buyCashItemCount: cashItems.reduce((s, i) => s + i.qty, 0),
            buyCreditItemCount: creditItems.reduce((s, i) => s + i.qty, 0),
        };
    }, [buyCartItems, buyTotalCash, buyTotalCredit, formatMoney]);

    const isEmpty = cart.length === 0;
    const hasSellItems = sellCartItems.length > 0;
    const hasBuyItems = buyCartItems.length > 0;

    // TKT-2026-103353
    const printReceiptEnabled = themeOverrides?.printReceiptEnabled === true;
    const qzAutoPrintReceipt = themeOverrides?.qzAutoPrintReceipt === true;

    // ─── ON-SCREEN KEYBOARD (TKT-2026-105056) ────────────────────────────────
    // Two independent halves, because they solve the problem for different stores.
    //
    // 1. `keyboardInset` always applies. It is how many pixels the device's own
    //    keyboard is covering, so the dialog can shrink out from under it instead
    //    of leaving the name and email fields hidden behind it.
    // 2. `onScreenKeyboard` is the store's opt-in. When it is on, the kiosk draws
    //    its own keyboard inside the dialog and tells the device not to open its
    //    one, so nothing can overlap in the first place.
    const keyboardInset = useKeyboardInset();
    const onScreenKeyboard = themeOverrides?.onScreenKeyboardEnabled === true;

    // Which field the on-screen keyboard is currently typing into.
    const [activeField, setActiveField] = useState(null);
    const [kbShift, setKbShift] = useState(true);
    const [kbSymbols, setKbSymbols] = useState(false);

    const FIELD_SETTERS = useMemo(() => ({
        name: setCustomerName,
        email: setCustomerEmail,
        phone: setCustomerPhone,
    }), []);

    const FIELD_LAYOUTS = { name: 'text', email: 'email', phone: 'tel' };

    const handleKeyboardKey = useCallback((ch) => {
        const setter = FIELD_SETTERS[activeField];
        if (!setter) return;
        setter((prev) => prev + ch);
        // Auto-shift is a one-shot, exactly as a phone keyboard behaves.
        setKbShift(false);
    }, [FIELD_SETTERS, activeField]);

    const handleKeyboardBackspace = useCallback(() => {
        const setter = FIELD_SETTERS[activeField];
        if (!setter) return;
        setter((prev) => prev.slice(0, -1));
    }, [FIELD_SETTERS, activeField]);

    const handleFieldFocus = useCallback((field) => {
        if (!onScreenKeyboard) return;
        setActiveField(field);
        setKbSymbols(false);
        // Capitalise the first letter of an empty name, nothing else.
        setKbShift(field === 'name');
    }, [onScreenKeyboard]);

    // Suppress the device keyboard only when ours is doing the job. `inputmode`
    // none is the supported way to ask for a text field with no virtual keyboard,
    // and it has to go through inputProps to land on the input element rather than
    // on the wrapper. A physical keyboard still types into the field, which is why
    // it is not made readOnly.
    const keyboardFieldProps = useCallback((field) => (
        onScreenKeyboard
            ? {
                inputProps: { inputMode: 'none' },
                onFocus: () => handleFieldFocus(field),
            }
            : {}
    ), [handleFieldFocus, onScreenKeyboard]);

    // Block the idle auto-reset while a checkout is submitting or the collection-code
    // screen is up, so an idle timeout never wipes an in-flight order or the code. (TKT-2026-103353)
    useEffect(() => {
        setTransactionInProgress(checkoutOpen || submitting || Boolean(successCode));
        return () => setTransactionInProgress(false);
    }, [checkoutOpen, submitting, successCode, setTransactionInProgress]);

    // Navigation handlers
    const handleGoBack = useCallback(() => navigate(`/${storeName}`), [navigate, storeName]);
    const handleBrowseProducts = useCallback(() => navigate(`/${storeName}`), [navigate, storeName]);

    // Unified checkout handlers
    const resetCheckoutDialogState = useCallback(() => {
        setCustomerName('');
        setCustomerEmail('');
        setCustomerPhone('');
        setErrorMsg('');
        setSuccessCode(null);
        setOrderHasBuyItems(false);
        setActiveField(null);
        setKbSymbols(false);
    }, []);

    const handleProceedToCheckout = useCallback(() => {
        resetCheckoutDialogState();
        setFinalizeCheckoutAfterClose(false);
        setCheckoutOpen(true);
    }, [resetCheckoutDialogState]);

    const handleCloseCheckoutDialog = useCallback(() => {
        if (submitting) {
            return;
        }

        if (successCode) {
            setFinalizeCheckoutAfterClose(true);
        }

        setCheckoutOpen(false);
    }, [submitting, successCode]);

    const handleCheckoutDialogExited = useCallback(() => {
        const shouldFinalizeCheckout = finalizeCheckoutAfterClose;

        resetCheckoutDialogState();
        setFinalizeCheckoutAfterClose(false);

        if (shouldFinalizeCheckout) {
            clearCart();
            navigate(`/${storeName}`);
        }
    }, [clearCart, finalizeCheckoutAfterClose, navigate, resetCheckoutDialogState, storeName]);

    const handleConfirmUnifiedOrder = async () => {
        if (!customerName.trim()) {
            setErrorMsg('Please enter your name.');
            return;
        }
        // Require email if there are buy items (needed for buylist)
        if (hasBuyItems && !customerEmail.trim()) {
            setErrorMsg('Please enter your email (required for buylist).');
            return;
        }
        // Optional on a sell-only order, but whatever is entered must be an address, on any order.
        if (customerEmail.trim() && !looksLikeAnEmail(customerEmail)) {
            setErrorMsg(hasBuyItems ? 'Please enter a valid email.' : 'Please enter a valid email, or leave it blank.');
            return;
        }
        if (!hasSellItems && !hasBuyItems) {
            setErrorMsg('Your cart is empty.');
            return;
        }

        setSubmitting(true);
        setErrorMsg('');

        try {
            // Build sell items payload
            const sellPayload = sellCartItems.map((i) => ({
                productId: i.productId,
                skuId: i.variant.skuId,
                quantity: i.qty,
                price: i.price,
                cleanName: i.cleanName,
                condAbbr: i.variant.condAbbr,
                printing: i.variant.printing,
                langAbbr: i.variant.langAbbr,
                // What the line shows. The server re-resolves catalog art for
                // this printing itself (routes/orders.js). TKT-2026-107421
                cdnImage: cartLineImage(i),
                groupName: i.groupName || '',
                categoryId: i.categoryId || '',
                categoryName: i.categoryName || '',
                number: i.number || '',
                rarity: i.rarity || '',
                setName: i.setName || i.groupName || '',
                comment: i.comment || '',
                remarks: i.remarks || '',
                cost: i.cost || '',
            }));

            // Build buy items payload
            const buyPayload = buyCartItems.map((i) => ({
                productId: i.productId,
                cleanName: i.cleanName,
                quantity: i.qty,
                skuId: i.variant?.skuId || null,
                condAbbr: i.variant.condAbbr,
                printing: i.variant.printing,
                langAbbr: i.variant.langAbbr || 'EN',
                paymentType: i.paymentType,
                buylistCashPrice: i.buylistCashPrice,
                buylistCreditPrice: i.buylistCreditPrice,
                cdnImage: cartLineImage(i),
                groupName: i.groupName || '',
                categoryId: i.categoryId || '',
                categoryName: i.categoryName || '',
                number: i.number || '',
                rarity: i.rarity || '',
                setName: i.setName || i.groupName || '',
                // Reference prices from buylist API for POS display
                marketPrice: i.marketPrice || 0,
                lowPrice: i.lowPrice || 0,
                midPrice: i.midPrice || 0,
                directLowPrice: i.directLowPrice || 0,
                highPrice: i.highPrice || 0,
                chosenPrice: i.chosenPrice || 0,
                chosenPriceType: i.chosenPriceType || '',
                // A slab: its grade is condAbbr, and the buylist re-quotes it
                // with this grader. A raw line carries none of these.
                // TKT-2026-107322
                ...(isGradedVariant(i.variant) ? {
                    isGraded: true,
                    gradingCompany: String(i.variant.gradingCompany).trim().toUpperCase(),
                    certNumber: i.variant.certNumber || '',
                } : {}),
            }));

            const response = await createOrder(storeName, {
                customerName: customerName.trim(),
                customerEmail: customerEmail.trim(),
                customerPhone: customerPhone.trim(),
                items: sellPayload.length > 0 ? sellPayload : [],
                buyItems: buyPayload.length > 0 ? buyPayload : [],
            });

            // The receipt prints what the buylist booked, not the kiosk's own
            // figures, should the two ever differ (TKT-2026-107051).
            applyBookedBuyLines(response.data.buyLines);
            setSuccessCode(response.data.collectionCode);
            setOrderHasBuyItems(response.data.hasBuyItems || false);
        } catch (err) {
            const data = err.response?.data || {};
            // TKT-2026-106839. The buylist refused the trade-in, so no order and
            // no code exist. Put the store's current offer on the refused lines
            // and keep the dialog open, so the customer sees the new total and
            // confirms again instead of walking away with a code for nothing.
            if (data.code === 'PRICE_NOT_OFFERED' && Array.isArray(data.priceViolations)) {
                const applied = applyBuylistRefusal(data.priceViolations);
                setErrorMsg(describeAppliedRefusal(applied, formatMoney));
            } else {
                setErrorMsg(data.msg || 'Failed to submit order. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    // TKT-2026-103353: build the collection-receipt HTML (with the collection code)
    // that the customer brings to the counter to collect and pay. Shared by silent
    // QZ Tray printing and the browser-print fallback.
    const buildReceiptHtml = useCallback(() => {
        if (!successCode) return null;
        const esc = (s) => String(s == null ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        const receiptStoreName = themeOverrides?.storeName || 'Order Receipt';
        const now = new Date().toLocaleString();

        // The set prints above the condition/printing, on its own line, because a set
        // name is far wider than a 280px thermal receipt. TKT-2026-105380.
        const detailLines = (i) => buildReceiptItemLines(i)
            .map((line) => `<br><span class="muted">${esc(line)}</span>`)
            .join('');

        const sellRows = sellCartItems.map((i) => {
            const lineTotal = formatMoney(i.price * i.qty);
            return `<tr><td>${esc(i.cleanName)}${detailLines(i)}</td><td class="c">${i.qty}</td><td class="r">${esc(formatMoney(i.price))}</td><td class="r">${esc(lineTotal)}</td></tr>`;
        }).join('');

        const buyRows = buyCartItems.map((i) => {
            const unit = i.paymentType === 'Credit' ? (i.buylistCreditPrice || 0) : (i.buylistCashPrice || 0);
            const lineTotal = formatMoney(unit * i.qty);
            return `<tr><td>${esc(i.cleanName)}${detailLines(i)}</td><td class="c">${i.qty}</td><td class="r">${esc(i.paymentType)}</td><td class="r">${esc(lineTotal)}</td></tr>`;
        }).join('');

        const rate = parseFloat(themeOverrides?.salesTaxRate) || 0;
        const netSubtotal = hasSellItems && hasBuyItems
            ? sellTotalPriceNum - buyTotalCash - buyTotalCredit
            : hasSellItems ? sellTotalPriceNum : 0;
        const taxBase = (hasSellItems && hasBuyItems && !themeOverrides?.taxOnNetTotal) ? sellTotalPriceNum : netSubtotal;
        const isInclusive = themeOverrides?.taxInclusive === true;
        const taxAmount = rate > 0 ? (isInclusive ? Math.max(0, taxBase * rate / (100 + rate)) : Math.max(0, taxBase * (rate / 100))) : 0;
        const totalWithTax = isInclusive ? netSubtotal : netSubtotal + taxAmount;

        const totalsRows = [];
        if (hasSellItems) totalsRows.push(`<div class="trow"><span>Purchase</span><span>${esc(formatMoney(sellTotalPriceNum))}</span></div>`);
        if (hasBuyItems && buyTotalCash > 0) totalsRows.push(`<div class="trow"><span>Buylist cash</span><span>-${esc(formatMoney(buyTotalCash))}</span></div>`);
        if (hasBuyItems && buyTotalCredit > 0) totalsRows.push(`<div class="trow"><span>Buylist credit</span><span>-${esc(formatMoney(buyTotalCredit))}</span></div>`);
        if (rate > 0) totalsRows.push(`<div class="trow"><span>Tax (${rate}%${isInclusive ? ' incl.' : ''})</span><span>${esc(formatMoney(taxAmount))}</span></div>`);
        const payLabel = (hasSellItems && hasBuyItems)
            ? (totalWithTax >= 0 ? 'You pay' : 'Store pays you')
            : hasSellItems ? 'Total' : 'Store pays you';
        const payValue = formatMoney(hasSellItems ? Math.abs(totalWithTax) : buyTotalCash + buyTotalCredit);
        totalsRows.push(`<div class="trow total"><span>${payLabel}</span><span>${esc(payValue)}</span></div>`);

        const html = `<!doctype html><html><head><meta charset="utf-8"><title>Receipt ${esc(successCode)}</title>
<style>
@page { margin: 0; }
* { box-sizing: border-box; }
body { font-family: -apple-system, 'Segoe UI', Roboto, Arial, sans-serif; width: 280px; margin: 0 auto; padding: 12px; color: #000; }
h1 { font-size: 15px; text-align: center; margin: 0 0 2px; }
.sub { text-align: center; font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: #333; margin-bottom: 8px; }
.code { text-align: center; font-size: 26px; font-weight: 800; letter-spacing: 0.18em; border: 2px dashed #000; border-radius: 8px; padding: 8px 4px; margin: 8px 0; }
.meta { font-size: 11px; color: #333; margin-bottom: 8px; }
table { width: 100%; border-collapse: collapse; font-size: 11px; }
th { text-align: left; border-bottom: 1px solid #000; padding: 3px 2px; }
td { padding: 4px 2px; border-bottom: 1px dotted #bbb; vertical-align: top; }
td.c { text-align: center; } td.r { text-align: right; white-space: nowrap; }
.muted { color: #666; font-size: 10px; }
.section { font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; margin: 10px 0 2px; }
.trow { display: flex; justify-content: space-between; font-size: 11px; padding: 2px 0; }
.trow.total { font-weight: 800; font-size: 13px; border-top: 1px solid #000; margin-top: 4px; padding-top: 4px; }
.foot { text-align: center; font-size: 11px; margin-top: 10px; }
</style></head><body>
<h1>${esc(receiptStoreName)}</h1>
<div class="sub">Pickup Receipt</div>
<div class="code">${esc(successCode)}</div>
<div class="meta">${esc(now)}${customerName ? '<br>Customer: ' + esc(customerName) : ''}</div>
${hasSellItems ? `<div class="section">To collect &amp; pay</div>
<table><thead><tr><th>Item</th><th class="c">Qty</th><th class="r">Price</th><th class="r">Total</th></tr></thead><tbody>${sellRows}</tbody></table>` : ''}
${hasBuyItems ? `<div class="section">Selling to store (verified at counter)</div>
<table><thead><tr><th>Item</th><th class="c">Qty</th><th class="r">Pay</th><th class="r">Amount</th></tr></thead><tbody>${buyRows}</tbody></table>` : ''}
<div style="margin-top:10px">${totalsRows.join('')}</div>
<div class="foot">Bring this code to the counter.<br>Payment due at counter.</div>
</body></html>`;
        return html;
    }, [successCode, customerName, sellCartItems, buyCartItems, formatMoney, themeOverrides, hasSellItems, hasBuyItems, sellTotalPriceNum, buyTotalCash, buyTotalCredit]);

    // Fallback: print the receipt via a hidden iframe and the browser print dialog.
    const printReceiptViaBrowser = useCallback((html) => {
        printReceiptHtmlViaBrowser(html);
    }, []);

    // Print the collection receipt. When QZ Tray auto-print is enabled we print
    // silently to the configured/default printer; on any failure (QZ not installed,
    // no printer, blocked) we fall back to the browser print dialog. When it is off we
    // use the browser dialog directly (the original behavior).
    const printReceipt = useCallback(async () => {
        const html = buildReceiptHtml();
        if (!html) return;
        if (qzAutoPrintReceipt) {
            try {
                await printReceiptHtmlViaQZ(html);
                return;
            } catch (e) {
                console.warn('QZ Tray receipt print failed, falling back to browser print:', e?.message || e);
            }
        }
        printReceiptViaBrowser(html);
    }, [qzAutoPrintReceipt, buildReceiptHtml, printReceiptViaBrowser]);

    // Warm up the QZ Tray connection when the checkout dialog opens so silent printing
    // is ready at submit time (and any one-time trust prompt happens up front).
    useEffect(() => {
        if (qzAutoPrintReceipt && checkoutOpen) initQZ();
    }, [qzAutoPrintReceipt, checkoutOpen]);

    // Auto-print once as soon as the order is placed, when QZ auto-print is enabled.
    const autoPrintedRef = useRef(false);
    useEffect(() => {
        if (successCode && qzAutoPrintReceipt && !autoPrintedRef.current) {
            autoPrintedRef.current = true;
            printReceipt();
        }
        if (!successCode) autoPrintedRef.current = false;
    }, [successCode, qzAutoPrintReceipt, printReceipt]);

    return (
        <ErrorBoundary>
            <Box
                sx={(theme) => ({
                    minHeight: '100vh',
                    background:
                        theme.palette.background.ownerPage
                        || (theme.palette.mode === 'light'
                            ? 'linear-gradient(180deg, rgba(248,250,255,0.9) 0%, rgba(226,232,240,0.92) 100%)'
                            : 'linear-gradient(180deg, rgba(15,23,42,0.94) 0%, rgba(17,24,39,0.9) 100%)'),
                    pb: 8,
                })}
            >
                <AppBar
                    position='static'
                    color='transparent'
                    elevation={0}
                    sx={(theme) => ({
                        mt: theme.spacing(3),
                        mx: 'auto',
                        px: theme.spacing(2),
                        py: theme.spacing(1.5),
                        width: 'clamp(320px, 96%, 1200px)',
                        borderRadius: '14px',
                        backgroundColor: hasBuyItems && !hasSellItems ? theme.palette.success.main : theme.palette.primary.main,
                        backgroundImage: 'none',
                        border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.2 : 0.3)}`,
                        boxShadow: hasBuyItems && !hasSellItems
                            ? `0 28px 60px ${alpha(theme.palette.success.main, 0.18)}`
                            : '0 28px 60px rgba(62,172,253,0.18)',
                    })}
                >
                    <Toolbar sx={{ gap: 2, flexWrap: 'wrap' }}>
                        <IconButton
                            edge='start'
                            onClick={handleGoBack}
                            aria-label='Go back to store'
                            sx={{
                                color: 'inherit',
                                border: (theme) => `1px solid ${alpha(theme.palette.common.white, 0.35)}`,
                                borderRadius: 2,
                            }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography variant='h5' fontWeight={700} letterSpacing='-0.01em'>
                                {hasBuyItems && hasSellItems ? 'Mixed Cart' : hasBuyItems ? 'Buylist Cart' : 'Checkout Cart'}
                            </Typography>
                            <Typography variant='body2' color='text.secondary'>
                                {hasBuyItems && hasSellItems
                                    ? 'Review your purchases and items to sell.'
                                    : hasBuyItems
                                        ? 'Review items you want to sell to us.'
                                        : 'Review selections and finalize your in-store pickup.'}
                            </Typography>
                        </Box>
                        {!isEmpty && (
                            <Stack direction='row' spacing={1}>
                                {hasSellItems && (
                                    <Chip
                                        label={`${sellTotalItems} to buy`}
                                        color='primary'
                                        variant='filled'
                                        sx={{ fontWeight: 600 }}
                                    />
                                )}
                                {hasBuyItems && (
                                    <Chip
                                        label={`${buyTotalItems} to sell`}
                                        sx={{ fontWeight: 600, backgroundColor: theme.palette.success.main, color: theme.palette.success.contrastText }}
                                    />
                                )}
                            </Stack>
                        )}
                        {!isEmpty && (
                            <Button
                                color='error'
                                onClick={() => {
                                    if (window.confirm('Are you sure you want to clear all items from your cart?')) {
                                        clearCart();
                                    }
                                }}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                Clear all
                            </Button>
                        )}
                    </Toolbar>
                </AppBar>

                <Container maxWidth='lg' sx={{ py: { xs: 4, md: 6 } }}>
                    {isEmpty ? (
                        <EmptyCart onBrowseProducts={handleBrowseProducts} />
                    ) : (
                        <Stack spacing={4} alignItems='center'>
                            {/* TWO-COLUMN GRID: Buys left, Sells right */}
                            <Grid container spacing={3} sx={{ width: '100%' }}>
                                {/* LEFT COLUMN — BUY ITEMS (Sell To Us) */}
                                {hasBuyItems && (
                                    <Grid size={{ xs: 12, md: hasSellItems ? 6 : 12 }}>
                                        <Paper
                                            elevation={0}
                                            sx={(theme) => ({
                                                p: { xs: 2.5, md: 3 },
                                                borderRadius: '14px',
                                                width: '100%',
                                                height: '100%',
                                                background:
                                                    theme.palette.background.ownerCard
                                                        ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.28)} 0%, ${alpha(theme.palette.success.main, 0.16)} 100%), ${theme.palette.background.ownerCard}`
                                                        : theme.palette.mode === 'light'
                                                            ? 'linear-gradient(135deg, rgba(232,245,233,0.98) 0%, rgba(200,230,201,0.92) 100%)'
                                                            : 'linear-gradient(135deg, rgba(27,94,32,0.3) 0%, rgba(46,125,50,0.2) 100%)',
                                                border: `2px solid ${theme.palette.success.main}`,
                                                boxShadow: '0 26px 60px rgba(76,175,80,0.14)',
                                            })}
                                        >
                                            <Stack spacing={1} sx={{ mb: 3 }}>
                                                <Typography variant='h6' fontWeight={700} sx={{ color: theme.palette.success.dark }}>
                                                    Sell To Us
                                                </Typography>
                                                <Typography variant='body2' color='text.secondary'>
                                                    Products you're selling to us.
                                                </Typography>
                                            </Stack>
                                            <List disablePadding>
                                                {buyCartItems.map((item) => (
                                                    <BuyCartItem
                                                        key={`${item.productId}-${variantKey(item.variant)}-${item.paymentType}`}
                                                        item={item}
                                                        onUpdateQuantity={updateQuantity}
                                                        onRemove={removeFromCart}
                                                        onUpdatePaymentType={updatePaymentType}
                                                    />
                                                ))}
                                            </List>
                                            {(themeOverrides?.buylistDisclaimer || 'Payout may vary depending on item condition upon inspection. Applicable taxes may be deducted from payout.') && (
                                                <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 2, lineHeight: 1.4 }}>
                                                    {themeOverrides?.buylistDisclaimer || 'Payout may vary depending on item condition upon inspection. Applicable taxes may be deducted from payout.'}
                                                </Typography>
                                            )}
                                        </Paper>
                                    </Grid>
                                )}

                                {/* RIGHT COLUMN — SELL ITEMS (Buy From Us) */}
                                {hasSellItems && (
                                    <Grid size={{ xs: 12, md: hasBuyItems ? 6 : 12 }}>
                                        <Paper
                                            elevation={0}
                                            sx={(theme) => ({
                                                p: { xs: 2.5, md: 3 },
                                                borderRadius: '14px',
                                                width: '100%',
                                                height: '100%',
                                                background:
                                                    theme.palette.background.ownerCard
                                                    || (theme.palette.mode === 'light'
                                                        ? 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(237,239,247,0.92) 100%)'
                                                        : 'linear-gradient(135deg, rgba(30,30,36,0.94) 0%, rgba(64,66,77,0.9) 100%)'),
                                                border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.12 : 0.24)}`,
                                                boxShadow: '0 26px 60px rgba(0,0,0,0.14)',
                                            })}
                                        >
                                            <Stack spacing={1} sx={{ mb: 3 }}>
                                                <Typography variant='h6' fontWeight={700}>
                                                    Buy From Us
                                                </Typography>
                                                <Typography variant='body2' color='text.secondary'>
                                                    Products you're buying from us.
                                                </Typography>
                                            </Stack>
                                            {/* TKT-2026-105225. Renders nothing unless the store has
                                                enabled cart optimisation. */}
                                            <CartOptimizerPanel
                                                storeName={storeName}
                                                settings={themeOverrides?.cartOptimization}
                                            />
                                            <List disablePadding>
                                                {sellCartItems.map((item) => (
                                                    <SellCartItem
                                                        // Full variant identity: a slab and a raw
                                                        // copy share a skuId, so a skuId key
                                                        // collided. TKT-2026-105367.
                                                        key={variantKey(item.variant)}
                                                        item={item}
                                                        onUpdateQuantity={updateQuantity}
                                                        onRemove={removeFromCart}
                                                    />
                                                ))}
                                            </List>
                                        </Paper>
                                    </Grid>
                                )}
                            </Grid>

                            {/* UNIFIED ORDER SUMMARY */}
                            {(hasSellItems || hasBuyItems) && (
                                <Paper
                                    elevation={0}
                                    sx={(theme) => ({
                                        p: 3,
                                        borderRadius: '14px',
                                        width: '100%',
                                        maxWidth: 640,
                                        mx: 'auto',
                                        background:
                                            theme.palette.background.ownerCard
                                            || (theme.palette.mode === 'light'
                                                ? 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(237,239,247,0.92) 100%)'
                                                : 'linear-gradient(135deg, rgba(30,30,36,0.94) 0%, rgba(64,66,77,0.9) 100%)'),
                                        border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.12 : 0.24)}`,
                                        boxShadow: '0 26px 60px rgba(0,0,0,0.14)',
                                    })}
                                >
                                    <Stack spacing={2}>
                                        <Typography variant='overline' fontWeight={700} color='text.secondary' textAlign='center'>
                                            Order Summary
                                        </Typography>
                                        {hasSellItems && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant='body2' color='text.secondary'>
                                                    Purchase ({sellTotalItems} {sellTotalItems === 1 ? 'item' : 'items'})
                                                </Typography>
                                                <Typography variant='body1' fontWeight={600} color='primary.onSurface'>
                                                    {sellTotalPrice}
                                                </Typography>
                                            </Box>
                                        )}
                                        {hasBuyItems && buyTotalCash > 0 && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant='body2' color='text.secondary'>
                                                    Buylist Cash ({buyCashItemCount} {buyCashItemCount === 1 ? 'item' : 'items'})
                                                </Typography>
                                                <Typography variant='body1' fontWeight={600} sx={{ color: theme.palette.success.dark }}>
                                                    -{buyTotalCashFormatted}
                                                </Typography>
                                            </Box>
                                        )}
                                        {hasBuyItems && buyTotalCredit > 0 && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant='body2' color='text.secondary'>
                                                    Buylist Credit ({buyCreditItemCount} {buyCreditItemCount === 1 ? 'item' : 'items'})
                                                </Typography>
                                                <Typography variant='body1' fontWeight={600} sx={{ color: theme.palette.success.dark }}>
                                                    -{buyTotalCreditFormatted}
                                                </Typography>
                                            </Box>
                                        )}
                                        {hasSellItems && hasBuyItems && (() => {
                                            const remainder = sellTotalPriceNum - buyTotalCash - buyTotalCredit;
                                            return (
                                                <>
                                                    <Divider />
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant='subtitle1' fontWeight={700}>
                                                            {remainder >= 0 ? 'You Pay' : 'Store Pays You'}
                                                        </Typography>
                                                        <Typography variant='h5' fontWeight={700} sx={{ color: remainder >= 0 ? 'primary.onSurface' : theme.palette.success.dark }}>
                                                            {formatMoney(Math.abs(remainder))}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant='caption' color='text.secondary'>
                                                        {remainder >= 0
                                                            ? 'Buylist credit will be applied toward your purchase total.'
                                                            : 'Remaining buylist value will be paid out at the counter.'}
                                                    </Typography>
                                                </>
                                            );
                                        })()}
                                        {(() => {
                                            const rate = parseFloat(themeOverrides?.salesTaxRate) || 0;
                                            if (rate <= 0) {
                                                return (
                                                    <Typography variant='caption' color='text.secondary' sx={{ textAlign: 'center', display: 'block' }}>
                                                        Plus applicable taxes
                                                    </Typography>
                                                );
                                            }
                                            const netSubtotal = hasSellItems && hasBuyItems
                                                ? sellTotalPriceNum - buyTotalCash - buyTotalCredit
                                                : hasSellItems ? sellTotalPriceNum : 0;
                                            const taxBase = (hasSellItems && hasBuyItems && !themeOverrides?.taxOnNetTotal)
                                                ? sellTotalPriceNum
                                                : netSubtotal;
                                            const isInclusive = themeOverrides?.taxInclusive === true;
                                            const taxAmount = isInclusive
                                                ? Math.max(0, taxBase * rate / (100 + rate))
                                                : Math.max(0, taxBase * (rate / 100));
                                            const totalWithTax = isInclusive ? netSubtotal : netSubtotal + taxAmount;
                                            return (
                                                <>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant='body2' color='text.secondary'>Tax ({rate}%{isInclusive ? ' incl.' : ''})</Typography>
                                                        <Typography variant='body1' fontWeight={600} color='text.secondary'>{formatMoney(taxAmount)}</Typography>
                                                    </Box>
                                                    <Divider />
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant='subtitle1' fontWeight={700}>Total</Typography>
                                                        <Typography variant='h5' fontWeight={700} color='primary.onSurface'>{formatMoney(totalWithTax)}</Typography>
                                                    </Box>
                                                </>
                                            );
                                        })()}
                                        <Divider />
                                        {orderLimit?.atLimit && (
                                            <Alert severity={overOrderLimit ? 'warning' : 'info'}>
                                                {overOrderLimit
                                                    ? `Your cart has ${orderLimit.units} ${orderLimit.units === 1 ? 'card' : 'cards'} (${orderLimit.lines} different). ${orderLimit.message}`
                                                    : orderLimit.message}
                                            </Alert>
                                        )}
                                        <Button
                                            variant='contained'
                                            size='large'
                                            fullWidth
                                            onClick={handleProceedToCheckout}
                                            disabled={overOrderLimit}
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                py: 1.6,
                                                fontSize: '1.1rem',
                                                background: overOrderLimit ? undefined : hasBuyItems && hasSellItems
                                                    ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.success.main})`
                                                    : hasBuyItems
                                                        ? theme.palette.success.main
                                                        : undefined,
                                                '&:hover': {
                                                    background: hasBuyItems && hasSellItems
                                                        ? `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.success.dark})`
                                                        : hasBuyItems
                                                            ? theme.palette.success.dark
                                                            : undefined,
                                                },
                                            }}
                                        >
                                            {hasBuyItems && hasSellItems
                                                ? 'Submit Unified Order'
                                                : hasBuyItems
                                                    ? 'Submit Buylist Order'
                                                    : 'Proceed to Checkout'
                                            }
                                        </Button>
                                    </Stack>
                                </Paper>
                            )}

                            <Button variant='text' onClick={handleBrowseProducts} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                Continue browsing
                            </Button>
                        </Stack>
                    )}
                </Container>

                {/* UNIFIED CHECKOUT DIALOG */}
                <Dialog
                    open={checkoutOpen}
                    onClose={handleCloseCheckoutDialog}
                    disableRestoreFocus
                    disableScrollLock
                    fullWidth
                    maxWidth='sm'
                    TransitionProps={{
                        // Wait for the modal to fully unmount before changing routes so
                        // iPad Safari does not keep a stale backdrop or scroll lock.
                        onExited: handleCheckoutDialogExited,
                    }}
                    // While the device keyboard is up, stop centring the dialog and
                    // pin it to the top of what is still visible. Centred, its lower
                    // half (the fields) sits behind the keyboard. TKT-2026-105056
                    sx={keyboardInset > 0 ? { alignItems: 'flex-start' } : undefined}
                    PaperProps={{
                        sx: (theme) => ({
                            borderRadius: '12px',
                            background:
                                theme.palette.background.ownerCard
                                || (theme.palette.mode === 'light'
                                    ? 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(237,242,255,0.9) 100%)'
                                    : 'linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(30,41,59,0.9) 100%)'),
                            border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.14 : 0.26)}`,
                            ...(keyboardInset > 0 && {
                                mt: 1,
                                mb: 1,
                                maxHeight: `calc(100% - ${keyboardInset + 16}px)`,
                            }),
                        }),
                    }}
                >
                    {successCode ? (
                        <>
                            <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, fontSize: '1.5rem' }}>
                                Order Submitted!
                            </DialogTitle>
                            <DialogContent sx={{ textAlign: 'center', pt: 2 }}>
                                <Typography variant='body1' color='text.secondary' sx={{ mb: 2 }}>
                                    Show this code at the counter to complete your transaction.
                                </Typography>
                                <Typography variant='h3' color='primary.onSurface' fontWeight={700} letterSpacing='0.2em'>
                                    {successCode}
                                </Typography>
                                {orderHasBuyItems && (
                                    <Typography variant='body2' sx={{ mt: 2, color: theme.palette.success.dark, fontWeight: 600 }}>
                                        Your buylist items will be verified at the counter.
                                    </Typography>
                                )}
                            </DialogContent>
                            <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                                {(printReceiptEnabled || qzAutoPrintReceipt) && (
                                    <Button
                                        onClick={printReceipt}
                                        variant='outlined'
                                        startIcon={<PrintIcon />}
                                        sx={{ textTransform: 'none', fontWeight: 600 }}
                                    >
                                        Print Receipt
                                    </Button>
                                )}
                                <Button onClick={handleCloseCheckoutDialog} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                    Done
                                </Button>
                            </DialogActions>
                        </>
                    ) : (
                        <>
                            <DialogTitle sx={{ fontWeight: 700 }}>
                                {hasBuyItems && hasSellItems ? 'Complete Unified Order' : hasBuyItems ? 'Submit Buylist' : 'Complete Checkout'}
                            </DialogTitle>
                            <DialogContent>
                                <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                                    {hasBuyItems
                                        ? 'Enter your contact information so we can process your order.'
                                        : 'Enter your name so staff can match the order in-store. Add your email to get an emailed receipt.'
                                    }
                                </Typography>
                                <Stack spacing={2}>
                                    <TextField
                                        autoFocus
                                        label='Name'
                                        type='text'
                                        fullWidth
                                        value={customerName}
                                        onChange={(e) => setCustomerName(e.target.value)}
                                        disabled={submitting}
                                        required
                                        {...keyboardFieldProps('name')}
                                    />
                                    {/* TKT-2026-103353: email is collected for every checkout so the
                                        customer can get an emailed copy of their order and code. It is
                                        required for buylist submissions and optional otherwise. */}
                                    <TextField
                                        label={hasBuyItems ? 'Email' : 'Email (optional)'}
                                        type='email'
                                        fullWidth
                                        value={customerEmail}
                                        onChange={(e) => setCustomerEmail(e.target.value)}
                                        disabled={submitting}
                                        required={hasBuyItems}
                                        helperText={hasBuyItems ? undefined : 'Add your email to receive a copy of your order and code.'}
                                        {...keyboardFieldProps('email')}
                                    />
                                    {hasBuyItems && (
                                        <TextField
                                            label='Phone (optional)'
                                            type='tel'
                                            fullWidth
                                            value={customerPhone}
                                            onChange={(e) => setCustomerPhone(e.target.value)}
                                            disabled={submitting}
                                            {...keyboardFieldProps('phone')}
                                        />
                                    )}
                                </Stack>

                                {/* The kiosk's own keyboard, drawn in the page so it can
                                    never sit on top of the fields. TKT-2026-105056 */}
                                {onScreenKeyboard && activeField && !submitting && (
                                    <KioskKeyboard
                                        layout={FIELD_LAYOUTS[activeField]}
                                        onKey={handleKeyboardKey}
                                        onBackspace={handleKeyboardBackspace}
                                        onDone={() => setActiveField(null)}
                                        shift={kbShift}
                                        onToggleShift={() => setKbShift((s) => !s)}
                                        symbols={kbSymbols}
                                        onToggleSymbols={() => setKbSymbols((s) => !s)}
                                    />
                                )}

                                {/* Order summary in dialog */}
                                <Box sx={{ mt: 3 }}>
                                    {hasSellItems && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant='body2' color='text.secondary'>
                                                Purchase ({sellTotalItems} {sellTotalItems === 1 ? 'item' : 'items'})
                                            </Typography>
                                            <Typography variant='body2' fontWeight={600} color='primary.onSurface'>
                                                {sellTotalPrice}
                                            </Typography>
                                        </Box>
                                    )}
                                    {hasBuyItems && (
                                        <>
                                            {buyTotalCash > 0 && (
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant='body2' color='text.secondary'>
                                                        Buylist Cash
                                                    </Typography>
                                                    <Typography variant='body2' fontWeight={600} sx={{ color: theme.palette.success.dark }}>
                                                        -{buyTotalCashFormatted}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {buyTotalCredit > 0 && (
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant='body2' color='text.secondary'>
                                                        Buylist Credit
                                                    </Typography>
                                                    <Typography variant='body2' fontWeight={600} sx={{ color: theme.palette.success.dark }}>
                                                        -{buyTotalCreditFormatted}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </>
                                    )}
                                    {hasSellItems && hasBuyItems && (() => {
                                        const remainder = sellTotalPriceNum - buyTotalCash - buyTotalCredit;
                                        return (
                                            <>
                                                <Divider sx={{ my: 1 }} />
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant='subtitle2' fontWeight={700}>
                                                        {remainder >= 0 ? 'You Pay' : 'Store Pays You'}
                                                    </Typography>
                                                    <Typography variant='subtitle2' fontWeight={700} sx={{ color: remainder >= 0 ? 'primary.onSurface' : theme.palette.success.dark }}>
                                                        {formatMoney(Math.abs(remainder))}
                                                    </Typography>
                                                </Box>
                                            </>
                                        );
                                    })()}
                                    {(() => {
                                        const rate = parseFloat(themeOverrides?.salesTaxRate) || 0;
                                        if (rate <= 0) return null;
                                        const netSubtotal = hasSellItems && hasBuyItems
                                            ? sellTotalPriceNum - buyTotalCash - buyTotalCredit
                                            : hasSellItems ? sellTotalPriceNum : 0;
                                        const taxBase = (hasSellItems && hasBuyItems && !themeOverrides?.taxOnNetTotal)
                                            ? sellTotalPriceNum
                                            : netSubtotal;
                                        const isInclusive = themeOverrides?.taxInclusive === true;
                                        const taxAmount = isInclusive
                                            ? Math.max(0, taxBase * rate / (100 + rate))
                                            : Math.max(0, taxBase * (rate / 100));
                                        const totalWithTax = isInclusive ? netSubtotal : netSubtotal + taxAmount;
                                        return (
                                            <>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                                    <Typography variant='caption' color='text.secondary'>Tax ({rate}%{isInclusive ? ' incl.' : ''})</Typography>
                                                    <Typography variant='caption' color='text.secondary'>{formatMoney(taxAmount)}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                                    <Typography variant='subtitle2' fontWeight={700}>Total</Typography>
                                                    <Typography variant='subtitle2' fontWeight={700} color='primary.onSurface'>{formatMoney(totalWithTax)}</Typography>
                                                </Box>
                                            </>
                                        );
                                    })()}
                                </Box>

                                {errorMsg && (
                                    <Typography variant='caption' color='error' sx={{ mt: 1, display: 'block' }}>
                                        {errorMsg}
                                    </Typography>
                                )}
                                {submitting && (
                                    <Typography variant='caption' color='text.secondary' sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                                        Processing your order… This may take a moment for large carts.
                                    </Typography>
                                )}
                            </DialogContent>
                            <DialogActions sx={{ px: 3, pb: 3 }}>
                                <Button onClick={handleCloseCheckoutDialog} disabled={submitting} sx={{ textTransform: 'none' }}>
                                    Cancel
                                </Button>
                                <Button
                                    variant='contained'
                                    onClick={handleConfirmUnifiedOrder}
                                    disabled={submitting}
                                    startIcon={submitting ? <CircularProgress size={20} /> : null}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        ...(hasBuyItems && !hasSellItems && {
                                            backgroundColor: theme.palette.success.main,
                                            '&:hover': { backgroundColor: theme.palette.success.dark },
                                        }),
                                        ...(hasBuyItems && hasSellItems && {
                                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.success.main})`,
                                            '&:hover': { background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.success.dark})` },
                                        }),
                                    }}
                                >
                                    {submitting ? 'Submitting…' : 'Confirm Order'}
                                </Button>
                            </DialogActions>
                        </>
                    )}
                </Dialog>
            </Box>
        </ErrorBoundary>
    );
}

CartPage.propTypes = {
    // relies on router params; no external props
};
